import React, { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { getLocalStorageWithExpiry, setLocalStorageWithExpiry } from '../utils/local-storage'
import { STORAGE_KEYS, STORAGE_TTL } from '../constants/storage-keys'
import { trackVatOption } from '../tracking/misc'
import { Logger } from '../utils/logger'

export interface VatContextProviderProps {
  vatToggleLabel?: string
  vatToggleInclusionText?: string
  vatToggleExclusionText?: string
}

type VatContextProps = VatContextProviderProps & {
  vatInclusive: boolean
  pricingContextScriptLoaded: boolean
  toggleVatInclusively: () => void
  setVatInclusively: (isInclusive: boolean) => void
}

const PCXT_V2_INITIALIZED = 'PCXT-ModuleInitialized'
const PCXT_V2_VAT_CHANGED = 'PCXT-VatInclusivityChanged'
const PCXT_V3_INITIALIZED = 'PCXT-V3-ModuleInitialized'
const PCXT_V3_VAT_CHANGED = 'PCXT-V3-VatInclusivityChanged'

export const VatContext = createContext({} as VatContextProps)

export const VatContextProvider: React.FC<VatContextProviderProps & { children: React.ReactNode }> = ({
  children,
  vatToggleLabel,
  vatToggleInclusionText,
  vatToggleExclusionText,
}) => {
  const [pricingContextScriptLoaded, setPricingContextScriptLoaded] = useState(false)
  const [vatInclusive, setVatInclusive] = useState<boolean>(false)

  useEffect(() => {
    const syncFromPricingContext = () => {
      const vatValue = document.pricingContextModule?.getPricingContext()?.vatInclusive ?? document.pcxtV3?.getPricingContext()?.vatInclusive
      if (vatValue !== undefined) {
        setVatInclusive(vatValue)
      }
      setPricingContextScriptLoaded(true)
    }

    const isAlreadyInitialized = document.pricingContextModule?.isInitialized() || document.pcxtV3?.isInitialized()

    if (isAlreadyInitialized) {
      syncFromPricingContext()
    }

    document.addEventListener(PCXT_V2_INITIALIZED, syncFromPricingContext)
    document.addEventListener(PCXT_V2_VAT_CHANGED, syncFromPricingContext)
    document.addEventListener(PCXT_V3_INITIALIZED, syncFromPricingContext)
    document.addEventListener(PCXT_V3_VAT_CHANGED, syncFromPricingContext)

    return () => {
      document.removeEventListener(PCXT_V2_INITIALIZED, syncFromPricingContext)
      document.removeEventListener(PCXT_V2_VAT_CHANGED, syncFromPricingContext)
      document.removeEventListener(PCXT_V3_INITIALIZED, syncFromPricingContext)
      document.removeEventListener(PCXT_V3_VAT_CHANGED, syncFromPricingContext)
    }
  }, [])

  const toggleVatInclusively = () => {
    const newValue = !vatInclusive

    // Optimistic local update so the UI responds immediately regardless of PCXT
    setVatInclusive(newValue)

    if (!getLocalStorageWithExpiry(STORAGE_KEYS.HIDE_VAT_SELECTION_BAR)) {
      setLocalStorageWithExpiry(STORAGE_KEYS.HIDE_VAT_SELECTION_BAR, true, STORAGE_TTL.ONE_MONTH)
    }

    // Propagate to pricing context module only once it has initialised
    if (pricingContextScriptLoaded) {
      document.pricingContextModule?.setVatInclusive(newValue)
      document.pcxtV3?.setVatInclusive(newValue)
    } else {
      Logger.instance.warning('[bookends-v2] Attempted to set VAT before pricing context module was initialized')
    }

    trackVatOption(newValue ? 'Include VAT' : 'Exclude VAT')
  }

  const setVatInclusively = (isVatInclusive: boolean) => {
    if (pricingContextScriptLoaded) {
      document.pricingContextModule?.setVatInclusive(isVatInclusive)
      document.pcxtV3?.setVatInclusive(isVatInclusive)
    } else {
      Logger.instance.warning('[bookends-v2] Attempted to set VAT before pricing context module was initialized')
    }
  }

  const contextValue = useMemo(
    () => ({
      vatToggleLabel,
      vatToggleInclusionText,
      vatToggleExclusionText,
      vatInclusive,
      pricingContextScriptLoaded,
      toggleVatInclusively,
      setVatInclusively,
    }),
    [vatToggleLabel, vatToggleInclusionText, vatToggleExclusionText, vatInclusive, pricingContextScriptLoaded],
  )

  return <VatContext.Provider value={contextValue}>{children}</VatContext.Provider>
}

export const useVatContext = () => useContext(VatContext)
